// Schedule Utilities for Fantasy Baseball

/**
 * Round a draft time string (HH:mm or H:mm) to nearest 5-minute interval.
 * Used so existing draft times display as 5-min options when step="300".
 */
export const roundDraftTimeTo5Min = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return hhmm || '';
  const parts = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!parts) return hhmm;
  const h = parseInt(parts[1], 10);
  const m = parseInt(parts[2], 10);
  const mm = Math.min(55, Math.round(m / 5) * 5);
  const hh = h >= 24 ? 23 : h;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/** Minute options for draft time: 00, 05, 10, ..., 55 */
export const DRAFT_TIME_MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/** Parse "HH:mm" or "H:mm" to { hour: 0-23, minute: 0|5|...|55 }. Defaults to 12:00. */
export const getDraftTimeParts = (hhmm) => {
  const rounded = roundDraftTimeTo5Min(hhmm || '');
  const parts = rounded.match(/^(\d{1,2}):(\d{2})$/);
  if (!parts) return { hour: 12, minute: 0 };
  const hour = Math.min(23, Math.max(0, parseInt(parts[1], 10)));
  const minute = Math.min(55, Math.round(parseInt(parts[2], 10) / 5) * 5);
  return { hour, minute };
};

/** Format hour (0-23) and minute (0,5,...,55) to "HH:mm". */
export const formatDraftTime = (hour, minute) => {
  const h = Math.min(23, Math.max(0, Number(hour)));
  const m = DRAFT_TIME_MINUTE_OPTIONS.includes(Number(minute)) ? Number(minute) : Math.round(Number(minute) / 5) * 5;
  const mm = Math.min(55, Math.max(0, m));
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/**
 * Calculate which period number contains the all-star break
 * All-star break is typically in mid-July (around July 10-15)
 * Returns period number based on weekly calculation (converted if needed)
 */
export const getAllStarBreakWeek = (seasonStartDate, matchupPeriod = 'weekly') => {
  if (!seasonStartDate) return null;
  
  const startDate = new Date(seasonStartDate + 'T00:00:00');
  const seasonYear = startDate.getFullYear();
  
  // All-star break dates by year (start date of the break)
  const allStarBreakDates = {
    2026: { month: 6, day: 13 }, // July 13, 2026
    2025: { month: 6, day: 14 }, // July 14, 2025
    2027: { month: 6, day: 12 }, // July 12, 2027 (estimated)
  };
  
  // Use specific date for known years, otherwise use mid-July (15th)
  const allStarInfo = allStarBreakDates[seasonYear] || { month: 6, day: 15 };
  const allStarDate = new Date(seasonYear, allStarInfo.month, allStarInfo.day);
  
  // If all-star date is before season start, use next year
  if (allStarDate < startDate) {
    const nextYearInfo = allStarBreakDates[seasonYear + 1] || { month: 6, day: 15 };
    const nextYearDate = new Date(seasonYear + 1, nextYearInfo.month, nextYearInfo.day);
    return getAllStarBreakWeekForDate(startDate, nextYearDate);
  }
  
  return getAllStarBreakWeekForDate(startDate, allStarDate);
};

// Helper function to calculate week number for all-star break
const getAllStarBreakWeekForDate = (seasonStart, allStarDate) => {
  // Convert to week number (assuming weekly matchups, Monday-Sunday)
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  
  const seasonStartMonday = getMonday(seasonStart);
  const allStarMonday = getMonday(allStarDate);
  const weeksDiff = Math.floor((allStarMonday - seasonStartMonday) / (1000 * 60 * 60 * 24 * 7));
  
  return weeksDiff + 1; // Week numbers are 1-indexed
};

/**
 * Check if a date falls within the all-star break period
 * All-star break is typically 3-4 days (Mon-Thu) in mid-July
 */
export const isAllStarBreakWeek = (date, seasonStartDate) => {
  if (!date || !seasonStartDate) return false;
  
  const checkDate = new Date(date + 'T00:00:00');
  const month = checkDate.getMonth(); // 0-indexed (June = 5, July = 6)
  const day = checkDate.getDate();
  
  // All-star break is typically July 10-15
  return month === 6 && day >= 10 && day <= 15;
};

/**
 * Get the actual period number for display, accounting for merged periods
 */
export const getDisplayWeekNumber = (periodNumber, mergeFirstTwoWeeks, allStarWeek, mergedWeeks = []) => {
  // If first two weeks are merged and we're past week 1, adjust
  if (mergeFirstTwoWeeks && weekNumber === 1) {
    return 1; // Week 1 becomes "Week 1-2"
  }
  if (mergeFirstTwoWeeks && weekNumber === 2) {
    return null; // Week 2 is merged, return null to skip
  }
  if (mergeFirstTwoWeeks && weekNumber > 2) {
    // All weeks after 2 are shifted down by 1
    return weekNumber - 1;
  }
  
  // If this week is merged with all-star break, return null
  if (mergedWeeks.includes(weekNumber)) {
    return null;
  }
  
  return weekNumber;
};

/**
 * Generate a random schedule for all periods
 * Ensures all teams are assigned (either matchup or bye) for each period
 * No team can appear twice in the same period
 */
export const generateRandomSchedule = (teams, totalPeriods, mergedPeriods = new Set(), periodMapping = {}, seasonStartDate = null, playoffStartPeriod = null) => {
  if (!teams || teams.length < 2) {
    return [];
  }

  const matchups = [];
  const teamIds = teams.map(t => t.id);
  
  for (let period = 1; period <= totalPeriods; period++) {
    // Stop generating if we hit the playoff start period
    if (playoffStartPeriod && period >= playoffStartPeriod) {
      break;
    }

    // Skip merged periods
    if (mergedPeriods.has(period)) {
      continue;
    }
    
    const displayPeriod = periodMapping[period] || period;
    
    // Shuffle teams for this period to randomize
    const shuffledTeams = [...teams].sort(() => Math.random() - 0.5);
    const usedTeamIds = new Set();
    let matchupIndex = 0;
    
    // Pair teams, ensuring each team appears exactly once per period
    for (let i = 0; i < shuffledTeams.length; i++) {
      const currentTeam = shuffledTeams[i];
      
      // Skip if this team is already used
      if (usedTeamIds.has(currentTeam.id)) {
        continue;
      }
      
      // Find an unused partner
      let partner = null;
      for (let j = i + 1; j < shuffledTeams.length; j++) {
        if (!usedTeamIds.has(shuffledTeams[j].id)) {
          partner = shuffledTeams[j];
          break;
        }
      }
      
      if (partner) {
        // Create a matchup with two teams
        matchups.push({
          id: `period-${displayPeriod}-matchup-${matchupIndex}`,
          week: displayPeriod, // Keep 'week' for backward compatibility
          period: displayPeriod,
          actualPeriod: period,
          team1Id: currentTeam.id,
          team2Id: partner.id,
          status: 'Scheduled'
        });
        usedTeamIds.add(currentTeam.id);
        usedTeamIds.add(partner.id);
        matchupIndex++;
      } else {
        // No partner available - this team gets a bye
        matchups.push({
          id: `period-${displayPeriod}-matchup-${matchupIndex}`,
          week: displayPeriod, // Keep 'week' for backward compatibility
          period: displayPeriod,
          actualPeriod: period,
          team1Id: currentTeam.id,
          team2Id: null, // Bye period
          status: 'Scheduled'
        });
        usedTeamIds.add(currentTeam.id);
        matchupIndex++;
      }
    }
    
    // Verify all teams are assigned exactly once
    const assignedTeamIds = new Set();
    const periodMatchups = matchups.filter(m => (m.period === displayPeriod || m.week === displayPeriod));
    periodMatchups.forEach(m => {
      if (m.team1Id) assignedTeamIds.add(m.team1Id);
      if (m.team2Id) assignedTeamIds.add(m.team2Id);
    });
    
    if (assignedTeamIds.size !== teamIds.length) {
      console.error(`Error: Not all teams assigned for period ${displayPeriod}. Assigned: ${assignedTeamIds.size}, Total: ${teamIds.length}`);
      // This should never happen with the logic above, but log if it does
    }
  }
  
  return matchups;
};

/**
 * Get the start/end Date objects for a given display period.
 * Handles merged periods (first two periods, all-star break)
 * Supports daily, weekly, and bi-weekly matchup periods.
 * Returns { start: Date, end: Date } or null if no season start date.
 * (Extracted from getMatchupDateRange so callers can enumerate the
 * individual days of a scoring period, e.g. the Live Matchup day strip.)
 */
export const getMatchupDateBounds = (displayPeriod, seasonStartDate, leagueSettings = {}) => {
  if (!seasonStartDate) return null;
  
  const matchupPeriod = leagueSettings?.schedule?.matchupPeriod || 'weekly';
  const seasonStart = new Date(seasonStartDate + 'T00:00:00');
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  
  const seasonStartMonday = getMonday(seasonStart);
  const mergeFirstTwoPeriods = leagueSettings?.schedule?.mergeFirstTwoWeeks !== false;
  const mergeAllStarBreak = leagueSettings?.schedule?.mergeAllStarBreak !== false;
  
  // Calculate all-star period if needed (still based on weekly calculation)
  const allStarPeriod = mergeAllStarBreak ? getAllStarBreakWeek(seasonStartDate) : null;
  
  let periodStart = new Date(seasonStartMonday);
  let periodEnd = new Date(seasonStartMonday);
  
  // Calculate days per period based on matchup period type
  const getDaysPerPeriod = () => {
    switch (matchupPeriod) {
      case 'daily':
        return 1;
      case 'bi-weekly':
        return 14;
      case 'weekly':
      default:
        return 7;
    }
  };
  
  const daysPerPeriod = getDaysPerPeriod();
  
  // Handle merged first two periods (only applies to weekly/bi-weekly)
  if (mergeFirstTwoPeriods && displayPeriod === 1 && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
    // Period 1 covers both period 1 and period 2
    periodStart.setDate(seasonStartMonday.getDate() + 0);
    periodEnd.setDate(seasonStartMonday.getDate() + (daysPerPeriod * 2) - 1);
  } else {
    // Calculate which actual period(s) this display period represents
    let actualPeriodStart = displayPeriod;
    let actualPeriodEnd = displayPeriod;
    
    // Calculate what display period the all-star period maps to
    // This must match the logic in calculateMergedWeeks
    let allStarDisplayPeriod = null;
    if (mergeAllStarBreak && allStarPeriod && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
      // Count how many actual periods map to display periods up to and including allStarPeriod
      let displayCount = 0;
      for (let actualPeriod = 1; actualPeriod <= allStarPeriod; actualPeriod++) {
        if (mergeFirstTwoPeriods && actualPeriod === 2) continue; // Skip merged period 2
        displayCount++;
      }
      allStarDisplayPeriod = displayCount;
    }
    
    // Adjust for merged first two periods
    if (mergeFirstTwoPeriods && displayPeriod > 1 && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
      actualPeriodStart = displayPeriod + 1;
      actualPeriodEnd = displayPeriod + 1;
    }
    
    // Handle all-star break merge (only for weekly/bi-weekly)
    // Use allStarDisplayPeriod for comparisons since displayPeriod is what we're comparing
    if (mergeAllStarBreak && allStarPeriod && allStarDisplayPeriod && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
      if (displayPeriod === allStarDisplayPeriod) {
        // The all-star period merges with the following period
        // So this display period covers actual periods allStarPeriod and allStarPeriod + 1
        actualPeriodStart = allStarPeriod;
        actualPeriodEnd = allStarPeriod + 1;
      } else if (displayPeriod === allStarDisplayPeriod + 1) {
        // This display period should not exist (it's merged into allStarDisplayPeriod)
        // But if it does exist, calculate dates for what would be the next period (allStarPeriod + 2)
        actualPeriodStart = allStarPeriod + 2;
        actualPeriodEnd = allStarPeriod + 2;
      } else if (displayPeriod > allStarDisplayPeriod) {
        // Periods after the merged all-star period need adjustment
        // actualPeriodStart is already adjusted for mergeFirstTwoPeriods
        // Now we need to account for the all-star merge
        if (actualPeriodStart === allStarPeriod + 1) {
          // This display period should have been skipped (it's merged into allStarDisplayPeriod)
          // But if we're here, calculate for what would be the next period (allStarPeriod + 2)
          actualPeriodStart = allStarPeriod + 2;
          actualPeriodEnd = allStarPeriod + 2;
        } else if (actualPeriodStart > allStarPeriod + 1) {
          // We're past the all-star merge, no additional adjustment needed
          // actualPeriodStart is already correct
        }
      }
    }
    
    // Calculate start date
    // For the all-star merged period, start on the Monday of the all-star week
    if (mergeAllStarBreak && allStarPeriod && allStarDisplayPeriod && displayPeriod === allStarDisplayPeriod && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
      // Get the actual all-star break date and find its Monday
      const seasonYear = seasonStart.getFullYear();
      const allStarBreakDates = {
        2026: { month: 6, day: 13 }, // July 13, 2026
        2025: { month: 6, day: 14 }, // July 14, 2025
        2027: { month: 6, day: 12 }, // July 12, 2027 (estimated)
      };
      const allStarInfo = allStarBreakDates[seasonYear] || { month: 6, day: 15 };
      const allStarDate = new Date(seasonYear, allStarInfo.month, allStarInfo.day);
      const allStarMonday = getMonday(allStarDate);
      periodStart.setTime(allStarMonday.getTime());
    } else {
      // Normal period calculation
      periodStart.setDate(seasonStartMonday.getDate() + (actualPeriodStart - 1) * daysPerPeriod);
    }
    
    // Calculate end date
    if (matchupPeriod === 'daily') {
      // Daily periods are just one day
      periodEnd = new Date(periodStart);
    } else {
      // Weekly or bi-weekly periods
      // For the all-star merged period, extend to the end of the following week (14 days total)
      // Use allStarDisplayPeriod for comparison
      if (mergeAllStarBreak && allStarPeriod && allStarDisplayPeriod && displayPeriod === allStarDisplayPeriod && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
        // This period covers both allStarPeriod and allStarPeriod + 1
        // End date should be the Sunday of the week following all-star (14 days total)
        periodEnd.setTime(periodStart.getTime());
        periodEnd.setDate(periodStart.getDate() + (daysPerPeriod * 2) - 1); // 2 weeks - 1 day (e.g., 14 days total)
      } else {
        // Normal period calculation
        periodEnd.setDate(seasonStartMonday.getDate() + (actualPeriodEnd - 1) * daysPerPeriod + (daysPerPeriod - 1));
      }
    }
  }
  
  return { start: periodStart, end: periodEnd };
};

/**
 * Get matchup date range for a given period as a display string
 * Handles merged periods (first two periods, all-star break)
 * Supports daily, weekly, and bi-weekly matchup periods
 */
export const getMatchupDateRange = (displayPeriod, seasonStartDate, leagueSettings = {}) => {
  const bounds = getMatchupDateBounds(displayPeriod, seasonStartDate, leagueSettings);
  if (!bounds) return '';

  const matchupPeriod = leagueSettings?.schedule?.matchupPeriod || 'weekly';
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (matchupPeriod === 'daily') {
    return formatDate(bounds.start);
  }

  return `${formatDate(bounds.start)} - ${formatDate(bounds.end)}`;
};

/**
 * Calculate the last period number of the regular season
 * Based on MLB regular season end date (typically early October)
 * Returns the display period number (accounting for merged periods)
 */
export const getLastRegularSeasonWeek = (seasonStartDate, leagueSettings = {}) => {
  if (!seasonStartDate) return 26; // Default fallback (approx 26 weeks for full MLB season)
  
  const seasonStart = new Date(seasonStartDate + 'T00:00:00');
  const seasonYear = seasonStart.getFullYear();
  
  // MLB regular season end dates by year (typically early October, around Oct 1-3)
  // Using the Sunday of the week containing the MLB regular season end
  const mlbRegularSeasonEndDates = {
    2026: { month: 9, day: 4 }, // October 4, 2026 (week containing regular season end)
    2025: { month: 9, day: 5 }, // October 5, 2025 (week containing regular season end)
    2027: { month: 9, day: 3 }, // October 3, 2027 (estimated)
  };
  
  // Use specific date for known years, otherwise use October 1st
  const seasonEndInfo = mlbRegularSeasonEndDates[seasonYear] || { month: 9, day: 1 }; // Month 9 = October (0-indexed)
  const regularSeasonEnd = new Date(seasonYear, seasonEndInfo.month, seasonEndInfo.day);
  
  // If season starts after the end date, use next year
  if (regularSeasonEnd < seasonStart) {
    const nextYearInfo = mlbRegularSeasonEndDates[seasonYear + 1] || { month: 9, day: 1 };
    const nextYearEnd = new Date(seasonYear + 1, nextYearInfo.month, nextYearInfo.day);
    return getLastRegularSeasonWeekForDate(seasonStart, nextYearEnd, leagueSettings);
  }
  
  return getLastRegularSeasonWeekForDate(seasonStart, regularSeasonEnd, leagueSettings);
};

// Helper function to calculate last week number
const getLastRegularSeasonWeekForDate = (seasonStart, seasonEnd, leagueSettings) => {
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  
  const seasonStartMonday = getMonday(seasonStart);
  // Find the Monday of the week containing the regular season end date
  const seasonEndMonday = getMonday(seasonEnd);
  
  // Calculate actual week number (1-indexed)
  const weeksDiff = Math.floor((seasonEndMonday - seasonStartMonday) / (1000 * 60 * 60 * 24 * 7));
  const actualLastWeek = weeksDiff + 1;
  
  // Convert to display week number (accounting for merged weeks)
  const mergeFirstTwoWeeks = leagueSettings?.schedule?.mergeFirstTwoWeeks !== false;
  const mergeAllStarBreak = leagueSettings?.schedule?.mergeAllStarBreak !== false;
  
  let displayLastWeek = actualLastWeek;
  
  // Adjust for merged first two weeks
  if (mergeFirstTwoWeeks && actualLastWeek > 2) {
    // Week 2 is merged into week 1, so subtract 1 from display week
    displayLastWeek = actualLastWeek - 1;
  }
  
  // Adjust for merged all-star break
  if (mergeAllStarBreak) {
    const seasonStartStr = seasonStart.toISOString().split('T')[0];
    const allStarWeek = getAllStarBreakWeek(seasonStartStr);
    if (allStarWeek && actualLastWeek > allStarWeek + 1) {
      // The week after all-star is merged, so subtract 1 more
      displayLastWeek = displayLastWeek - 1;
    }
  }
  
  return displayLastWeek;
};

/**
 * Calculate merged periods based on schedule settings
 */
export const calculateMergedWeeks = (leagueSettings, totalPeriods) => {
  const mergedPeriods = new Set();
  const periodMapping = {};
  
  // Default to true if not explicitly set to false
  const mergeFirstTwoPeriods = leagueSettings?.schedule?.mergeFirstTwoWeeks !== false;
  const mergeAllStarBreak = leagueSettings?.schedule?.mergeAllStarBreak !== false;
  const seasonStartDate = leagueSettings?.season?.startDate;
  
  // Handle first two periods merge (only for weekly/bi-weekly)
  const matchupPeriod = leagueSettings?.schedule?.matchupPeriod || 'weekly';
  
  // Get all-star period if needed
  const allStarPeriod = (mergeAllStarBreak && seasonStartDate && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly'))
    ? getAllStarBreakWeek(seasonStartDate)
    : null;
  
  // Build period mapping step by step
  let displayPeriod = 1;
  
  for (let actualPeriod = 1; actualPeriod <= totalPeriods; actualPeriod++) {
    // Skip if this period is merged
    if (mergeFirstTwoPeriods && actualPeriod === 2 && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
      // Period 2 is merged into period 1, skip it
      mergedPeriods.add(2);
      continue;
    }
    
    if (mergeAllStarBreak && allStarPeriod && actualPeriod === allStarPeriod + 1 && (matchupPeriod === 'weekly' || matchupPeriod === 'bi-weekly')) {
      // The period after all-star is merged into the all-star period, skip it
      mergedPeriods.add(allStarPeriod + 1);
      continue;
    }
    
    // Map this actual period to the current display period
    periodMapping[actualPeriod] = displayPeriod;
    displayPeriod++;
  }
  
  return { mergedWeeks: mergedPeriods, weekMapping: periodMapping };
};

